import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import * as Sentry from "../lib/sentry";
import React, { useState } from "react";
import { logFeedbackSent } from "../utils/analyticsUtils";
import { SentryFeedbackAttachmentList } from "./SentryFeedbackAttachmentList";
import {
  fileToAttachment,
  isValidScreenshotFile,
  MAX_SCREENSHOT_COUNT,
  SCREENSHOT_FILE_ACCEPT,
  SCREENSHOT_VALIDATION_ERROR,
} from "./sentryFeedbackAttachments";

type SentryFeedbackModalProps = {
  visible: boolean;
  onClose: () => void;
};

export function SentryFeedbackModal({
  visible,
  onClose,
}: SentryFeedbackModalProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);

  const resetForm = () => {
    setName("");
    setEmail("");
    setMessage("");
    setScreenshots([]);
    setError("");
    setIsSending(false);
  };

  const closeForm = () => {
    onClose();
    resetForm();
  };

  const chooseScreenshots = () => {
    if (typeof document === "undefined") {
      setError("Screenshot upload is only available in the web app.");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = SCREENSHOT_FILE_ACCEPT;
    input.multiple = true;
    input.onchange = () => {
      const selectedFiles = Array.from(input.files ?? []);
      const validFiles = selectedFiles.filter(isValidScreenshotFile);

      setScreenshots((currentScreenshots) =>
        [...currentScreenshots, ...validFiles].slice(0, MAX_SCREENSHOT_COUNT),
      );
      setError(
        validFiles.length === selectedFiles.length
          ? ""
          : SCREENSHOT_VALIDATION_ERROR,
      );
    };
    input.click();
  };

  const submitFeedback = async () => {
    if (!message.trim() || isSending) {
      return;
    }

    try {
      setIsSending(true);
      setError("");

      const attachments = await Promise.all(screenshots.map(fileToAttachment));

      await Sentry.sendFeedback(
        {
          message: message.trim(),
          name: name.trim() || undefined,
          email: email.trim() || undefined,
          url:
            Platform.OS === "web" && typeof window !== "undefined"
              ? window.location.href
              : undefined,
          source: "custom_feedback_form",
          tags: {
            attachment_count: attachments.length,
          },
        },
        {
          attachments,
          includeReplay: true,
        },
      );

      logFeedbackSent("feedback_form", attachments.length);
      closeForm();
    } catch (submitError) {
      console.error("Failed to send feedback:", submitError);
      setError("Failed to send feedback. Please try again.");
      setIsSending(false);
    }
  };

  const canSubmit = Boolean(message.trim()) && !isSending;

  return (
    <Modal
      animationType="none"
      transparent
      visible={visible}
      onRequestClose={closeForm}
    >
      <Pressable style={styles.overlay} onPress={closeForm}>
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          style={{ width: "100%", maxWidth: 360 }}
        >
          <Pressable style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.title}>Feedback</Text>
              <Pressable style={styles.closeButton} onPress={closeForm}>
                <Text style={styles.closeText}>x</Text>
              </Pressable>
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.label}>Name</Text>
              <TextInput
                style={styles.input}
                placeholder="Your Name"
                placeholderTextColor="#C9C2D4"
                value={name}
                onChangeText={setName}
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.label}>Email</Text>
              <TextInput
                style={styles.input}
                autoCapitalize="none"
                keyboardType="email-address"
                placeholder="your.email@example.org"
                placeholderTextColor="#C9C2D4"
                value={email}
                onChangeText={setEmail}
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.label}>Description (required)</Text>
              <TextInput
                style={[styles.input, styles.messageInput]}
                multiline
                placeholder="Message"
                placeholderTextColor="#C9C2D4"
                value={message}
                onChangeText={setMessage}
              />
            </View>

            {/* Screenshot picking uses a DOM file input; native has no picker yet. */}
            {Platform.OS === "web" && (
              <TouchableOpacity
                style={styles.secondaryButton}
                onPress={chooseScreenshots}
              >
                <Text style={styles.secondaryText}>Add a screenshot</Text>
              </TouchableOpacity>
            )}

            <SentryFeedbackAttachmentList
              screenshots={screenshots}
              onRemove={(index) =>
                setScreenshots((currentScreenshots) =>
                  currentScreenshots.filter(
                    (_, screenshotIndex) => screenshotIndex !== index,
                  ),
                )
              }
            />

            {error ? <Text style={styles.errorText}>{error}</Text> : null}

            <TouchableOpacity
              style={[
                styles.submitButton,
                !canSubmit ? styles.disabledButton : null,
              ]}
              disabled={!canSubmit}
              onPress={submitFeedback}
            >
              <Text style={styles.submitText}>
                {isSending ? "Sending..." : "Send Feedback"}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={closeForm}
            >
              <Text style={styles.secondaryText}>Cancel</Text>
            </TouchableOpacity>
          </Pressable>
        </KeyboardAvoidingView>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "flex-end",
    alignItems: "flex-end",
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 360,
    backgroundColor: "#2C2533",
    borderRadius: 20,
    padding: 22,
    gap: 10,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    color: "#F6F7F9",
    fontSize: 22,
    fontFamily: "Lexend",
    fontWeight: "600",
    lineHeight: 28,
  },
  closeButton: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
  },
  closeText: {
    color: "#F6F7F9",
    fontSize: 24,
    lineHeight: 28,
  },
  fieldGroup: {
    gap: 8,
  },
  label: {
    color: "#F6F7F9",
    fontSize: 13,
    fontFamily: "Lexend",
    lineHeight: 18,
  },
  input: {
    borderWidth: 1,
    borderColor: "rgba(246, 247, 249, 0.18)",
    borderRadius: 8,
    color: "#F6F7F9",
    fontSize: 14,
    fontFamily: "Lexend",
    paddingHorizontal: 13,
    paddingVertical: 9,
  },
  messageInput: {
    minHeight: 122,
    textAlignVertical: "top",
  },
  secondaryButton: {
    borderWidth: 1,
    borderColor: "rgba(246, 247, 249, 0.18)",
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  secondaryText: {
    color: "#F6F7F9",
    fontSize: 14,
    fontFamily: "Lexend",
    lineHeight: 20,
    textAlign: "center",
  },
  errorText: {
    color: "#FFB4AB",
    fontSize: 13,
    fontFamily: "Lexend",
    lineHeight: 18,
  },
  submitButton: {
    backgroundColor: "#6650D1",
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 11,
    paddingHorizontal: 14,
  },
  disabledButton: {
    opacity: 0.55,
  },
  submitText: {
    color: "#F6F7F9",
    fontSize: 14,
    fontFamily: "Lexend",
    lineHeight: 20,
  },
});
